import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useRef } from 'react';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  viewport: { once: true, margin: '-100px' },
};

const HomePage = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.8, 0.3]);

  const featuredProducts = [
    {
      name: 'Enterprise Printer',
      category: 'Printing Solutions',
      image: process.env.PUBLIC_URL + '/images/product_printer.webp',
    },
    {
      name: 'Smart Photocopier',
      category: 'Document Management',
      image: process.env.PUBLIC_URL + '/images/product_copier.webp',
    },
    {
      name: 'Professional Scanner',
      category: 'Digital Archiving',
      image: process.env.PUBLIC_URL + '/images/product_scanner.webp',
    },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section
        ref={heroRef}
        data-testid="hero-section"
        className="relative h-screen flex items-center justify-center overflow-hidden"
      >
        <motion.div
          style={{ y }}
          className="absolute inset-0 z-0"
        >
          <div
            className="absolute inset-0 bg-cover bg-center img-desaturate"
            style={{
              backgroundImage: `url('${process.env.PUBLIC_URL}/images/hero_bg.webp')`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#F2F0E9]/30 via-[#F2F0E9]/60 to-[#F2F0E9]" />
        </motion.div>

        <motion.div
          style={{ opacity }}
          className="relative z-10 text-center px-6 max-w-5xl"
        >
          <motion.h1
            data-testid="hero-title"
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="hero-title text-[#1A1A1A] mb-8"
          >
            Excellence in <span className="italic">Every</span> Page
          </motion.h1>
          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="text-xl md:text-2xl text-[#595959] max-w-3xl mx-auto leading-relaxed"
          >
            Quality Services delivers premium printing and scanning solutions. We provide advanced equipment and reliable service to power your business.
          </motion.p>
        </motion.div>
      </section>

      {/* Introduction Section */}
      <section data-testid="intro-section" className="py-24 md:py-32 bg-[#F2F0E9]">
        <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeInUp} className="max-w-4xl mx-auto text-center">
            <h2 className="section-title text-[#1A1A1A] mb-12 accent-cyan">
              Industrial Utility Meets Luxury
            </h2>
            <p className="text-lg md:text-xl text-[#595959] leading-relaxed mb-8">
              For over two decades, we've been transforming the way businesses approach document management. As an authorized Xerox franchise, we bring world-class technology with a personal touch.
            </p>
            <p className="text-lg md:text-xl text-[#595959] leading-relaxed">
              From cutting-edge printers to intelligent photocopiers and high-speed scanners, every solution is crafted to elevate your workflow.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Products */}
      <section data-testid="featured-products-section" className="py-24 md:py-32 bg-[#EBE8E1]">
        <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
          <motion.h2
            {...fadeInUp}
            className="section-title text-[#1A1A1A] mb-16 accent-magenta"
          >
            Featured Solutions
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {featuredProducts.map((product, index) => (
              <motion.div
                key={index}
                data-testid={`featured-product-${index}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2, ease: [0.22, 1, 0.36, 1] }}
                viewport={{ once: true }}
                className="card-hover group cursor-pointer"
              >
                <div className="aspect-[4/5] overflow-hidden bg-white">
                  <img
                    loading="lazy"
                    decoding="async"
                    src={product.image}
                    alt={product.name}
                    width="400"
                    height="500"
                    className="w-full h-full object-cover img-desaturate"
                  />
                </div>
                <div className="py-6">
                  <p className="text-xs uppercase tracking-widest text-[#595959] mb-2">
                    {product.category}
                  </p>
                  <h3 className="serif text-2xl text-[#1A1A1A]">{product.name}</h3>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeInUp} className="text-center mt-16">
            <Link to="/products" data-testid="view-all-products-btn" className="luxury-button inline-flex items-center gap-2">
              View All Products
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-24 md:py-32 bg-[#1A1A1A] text-[#F2F0E9]">
        <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.h2
            {...fadeInUp}
            className="section-title mb-8"
          >
            Ready to Transform Your Workflow?
          </motion.h2>
          <motion.p
            {...fadeInUp}
            className="text-lg md:text-xl text-[#EBE8E1] max-w-2xl mx-auto mb-12"
          >
            Let's discuss how our premium solutions can elevate your business operations.
          </motion.p>
          <motion.div {...fadeInUp}>
            <Link
              to="/contact"
              data-testid="cta-contact-btn"
              className="border border-[#F2F0E9] bg-transparent text-[#F2F0E9] px-8 py-4 uppercase tracking-widest text-xs font-medium inline-block transition-all duration-500 hover:bg-[#F2F0E9] hover:text-[#1A1A1A]"
            >
              Get in Touch
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
